import walmartLogo from "/img/integration/walmart.png"
import { ConnectLogo } from "../connect-logo"
import { Form } from "@/components/form/Form.tsx"
import { useFormState } from "@/components/form/form_state.tsx"
import { Input, PasswordInput } from "@/components/form/Input.tsx"
import { Button } from "@/components/ui/button.tsx"
import { useState } from "preact/compat"
import { useLocation } from "wouter-preact"
import { use_connect_redirect_to } from "../use_connect_redirect_to"
import { Alerts } from "@/components/Alerts.tsx"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { CheckCircleIcon } from "@heroicons/react/20/solid"
import { upsertIntegration } from "@/api/integration"
import { HighRadiusLoginSection } from "./highradius_section"

/** Where a Retail Link site admin creates the bot user and generates its token. */
const USER_MANAGEMENT_URL = "https://supplierone.wal-mart.com/"
const BOT_HELP_URL = "https://supplierone.wal-mart.com/help/supplierone/article/dq78bz0on4-supplier-bot-management"

/**
 * Credential payload for POST /integration when connecting Walmart.
 *
 * A bot account authenticates with its password **plus** the bot token, which is what replaces MFA —
 * so both are secrets and both live on the credential, never in integration data.
 */
export function buildWalmartIntegrationCredentials(password: string, bot_token: string): Record<string, string> {
  return {
    password: password.trim(),
    bot_token: bot_token.trim(),
  }
}

function StepIndicator({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) {
  return (
    <div class="flex items-center justify-center gap-1.5 mb-6">
      {Array.from({ length: totalSteps }, (_, i) => (
        <div
          key={i}
          class={`h-1.5 w-6 rounded-full transition-colors ${
            i < currentStep ? "bg-gray-900" : i === currentStep ? "bg-gray-400" : "bg-gray-200"
          }`}
        />
      ))}
    </div>
  )
}

function StepNav({
  onBack,
  nextLabel = "Next",
  submit = false,
  onNext,
  disabled,
}: {
  onBack: () => void
  nextLabel?: string
  submit?: boolean
  onNext?: () => void
  disabled?: boolean
}) {
  return (
    <div class="flex gap-3">
      <Button type="button" variant="outline" class="flex-1" onClick={onBack}>
        Back
      </Button>
      <Button
        type={submit ? "submit" : "button"}
        class="flex-1 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-300"
        onClick={onNext}
        disabled={disabled}>
        {nextLabel}
      </Button>
    </div>
  )
}

function HelpLink({ children }: { children: preact.ComponentChildren }) {
  return (
    <a href={BOT_HELP_URL} target="_blank" rel="noopener noreferrer" class="text-gray-900 underline">
      {children}
    </a>
  )
}

const TOTAL_STEPS = 5

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ConnectWalmartPage(props: any = {}) {
  const redirectTo = use_connect_redirect_to(props.redirectTo)
  const [, setLocation] = useLocation()
  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [highradius_saved, set_highradius_saved] = useState(false)

  const form = useFormState({
    username: "",
    password: "",
    bot_token: "",
  })

  async function handleConfirm() {
    if (!form.username.value.trim() || !form.password.value.trim() || !form.bot_token.value.trim()) {
      form.set_form_errors(["Enter the bot account's User ID, password, and bot token"])
      return
    }

    setLoading(true)
    form.set_form_errors([])

    // The server signs in with these before saving the connection, so a failure here is Walmart
    // telling us exactly what's wrong (bad token, expired token, locked account).
    const res = await upsertIntegration(
      "walmart",
      form.username.value,
      buildWalmartIntegrationCredentials(form.password.value, form.bot_token.value)
    )

    if (!res.ok) {
      setLoading(false)
      form.set_form_errors([res.error.message || "Walmart rejected the bot account. Check the token and try again."])
      return
    }

    setLoading(false)
    setCompleted(true)
  }

  if (completed) {
    return (
      <CenterColumnContainer>
        <div class="text-center space-y-6">
          <ConnectLogo src={walmartLogo} alt="Walmart" />
          <div class="flex justify-center">
            <CheckCircleIcon class="h-12 w-12 text-green-500" />
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Walmart Connected</h3>
            <p class="text-gray-500 text-sm mt-1">
              We signed in with your bot account. Heads up: Walmart expires bot tokens after 60 days — we&apos;ll warn
              you before then so your sync doesn&apos;t stop.
            </p>
          </div>
          {/* Retail Link is half of Walmart: the bills behind its deductions live in HighRadius. */}
          <div class="text-left border-t border-gray-200 pt-6 space-y-3">
            <h3 class="text-base font-semibold text-gray-900">One more: your HighRadius login</h3>
            <HighRadiusLoginSection onSaved={() => set_highradius_saved(true)} />
          </div>
          <Button
            class="w-full bg-gray-900 text-white rounded-lg hover:bg-gray-800"
            onClick={() => setLocation(redirectTo)}>
            {highradius_saved ? "Done" : "Skip for now"}
          </Button>
        </div>
      </CenterColumnContainer>
    )
  }

  return (
    <CenterColumnContainer>
      <div class="space-y-6">
        <ConnectLogo src={walmartLogo} alt="Walmart" />
        <StepIndicator currentStep={step} totalSteps={TOTAL_STEPS} />

        {step === 0 && (
          <div class="space-y-4">
            <h3 class="text-base font-semibold text-gray-900">Step 1: Create a bot user in Retail Link</h3>
            <p class="text-gray-500 text-sm">
              Walmart requires automated access to use a <strong>bot account</strong> — a separate User ID from your
              own. It needs its own company email address, and only a Retail Link account administrator can create one.
            </p>
            <ol class="text-sm text-gray-500 space-y-1.5 list-decimal list-inside">
              <li>
                In Retail Link, open{" "}
                <a href={USER_MANAGEMENT_URL} target="_blank" rel="noopener noreferrer" class="text-gray-900 underline">
                  User Management
                </a>{" "}
                and choose <strong>Add User</strong>.
              </li>
              <li>
                Check <strong>&ldquo;This is a Bot User Account&rdquo;</strong>, then click Add User.
              </li>
              <li>The new bot user sets its own password from the invitation email.</li>
            </ol>
            <p class="text-xs text-gray-400">
              Signing in as the bot will say it&apos;s a Bot User Account — that&apos;s expected. See{" "}
              <HelpLink>Walmart&apos;s guide</HelpLink>.
            </p>
            <Button class="w-full bg-gray-900 text-white rounded-lg hover:bg-gray-800" onClick={() => setStep(1)}>
              Next
            </Button>
            <p class="text-xs text-gray-400 text-center">
              No bot account yet?{" "}
              <a href="/setup/connect/walmart-highradius" class="text-gray-900 underline">
                Add your HighRadius login
              </a>{" "}
              on its own.
            </p>
          </div>
        )}

        {step === 1 && (
          <div class="space-y-4">
            <h3 class="text-base font-semibold text-gray-900">Step 2: Generate the bot token</h3>
            <p class="text-gray-500 text-sm">
              The bot token is what lets us sign in without a two-factor code — no phone number needed.
            </p>
            <ol class="text-sm text-gray-500 space-y-1.5 list-decimal list-inside">
              <li>
                In User Management, find the bot user, click the <strong>…</strong> menu and choose{" "}
                <strong>View Profile</strong>.
              </li>
              <li>
                Click <strong>Generate Bot Token</strong>, then copy the token.
              </li>
            </ol>
            <StepNav onBack={() => setStep(0)} onNext={() => setStep(2)} />
          </div>
        )}

        {step === 2 && (
          <Form class="space-y-4" onSubmit={() => setStep(3)} alerts={form.form_alerts}>
            <h3 class="text-base font-semibold text-gray-900">Step 3: Bot account credentials</h3>
            <p class="text-gray-500 text-sm">
              The bot user&apos;s User ID (its email address) and the password it set.
            </p>
            <Input label="Bot User ID" type="text" name="username" required={true} bind={form.username} noAutofill />
            <PasswordInput label="Password" required={true} bind={form.password} noAutofill />
            <StepNav onBack={() => setStep(1)} submit={true} />
          </Form>
        )}

        {step === 3 && (
          <Form class="space-y-4" onSubmit={() => setStep(4)} alerts={form.form_alerts}>
            <h3 class="text-base font-semibold text-gray-900">Step 4: Bot token</h3>
            <p class="text-gray-500 text-sm">Paste the token you copied from the bot user&apos;s profile.</p>
            <Input label="Bot token" type="text" name="bot_token" required={true} bind={form.bot_token} noAutofill />
            <p class="text-xs text-gray-400">
              The token carries its own expiry date (60 days from when it was generated), so there is nothing else to
              enter — we read the date and your vendor number from Walmart.
            </p>
            <StepNav onBack={() => setStep(2)} submit={true} />
          </Form>
        )}

        {step === 4 && (
          <div class="space-y-4">
            <h3 class="text-base font-semibold text-gray-900">Step 5: Confirm</h3>
            <p class="text-gray-500 text-sm">We&apos;ll sign in to Retail Link with the bot account to check it works.</p>
            <ul class="text-sm text-gray-500 space-y-1.5 list-disc list-inside">
              <li>
                Bot User ID: <span class="font-medium text-gray-700">{form.username.value || "(not entered)"}</span>
              </li>
              <li>
                Bot token: <span class="font-medium text-gray-700">{form.bot_token.value ? "saved" : "(not entered)"}</span>
              </li>
            </ul>
            <Alerts alerts={form.form_alerts} />
            <StepNav
              onBack={() => setStep(3)}
              onNext={handleConfirm}
              nextLabel={loading ? "Checking with Walmart..." : "Confirm"}
              disabled={loading}
            />
          </div>
        )}
      </div>
    </CenterColumnContainer>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("buildWalmartIntegrationCredentials", () => {
    it("saves the password and bot token as secrets", () => {
      expect(buildWalmartIntegrationCredentials(" pw ", " tok-123 ")).toEqual({
        password: "pw",
        bot_token: "tok-123",
      })
    })
  })
}
